import { get, has, isArray, isObject } from "lodash";

export const defaultErrorMessage =
  "Something went wrong. Please try again later.";

/**
 * One entry of an error list as amphitheater sends it: a text, or an object
 * holding one (`api_error_response({"message": "…"})` makes
 * `{error: {message: [{message: "…"}]}}`).
 */
export type ServerErrorEntry = string | { message?: unknown; detail?: unknown };

export interface ServerError {
  status?: number;
  data?: ServerError;
  message?: string | Record<string, string> | string[];
  error?: {
    message?: string | ServerErrorEntry | ServerErrorEntry[];
    error?: string | Record<string, string> | string[];
    detail?: string | Record<string, string> | string[];
    [key: string]: unknown;
  };
  detail?: string | Record<string, string> | string[];
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export const convertToError = (e: any) => {
  let error;
  if (e instanceof Error) {
    error = e;
  } else if (typeof e === "string") {
    error = new Error(e);
  } else if (typeof e === "object") {
    error = new Error(JSON.stringify(e));
  } else {
    error = new Error("No error instance thrown for operation: " + String(e));
  }

  return error;
};

export const isErrorWithMessageObject = (e: any) => {
  return has(e, "error.message") && isObject(e?.error?.message);
};

export const isErrorWithDataAndError = (e: any) => {
  return has(e, "data.error");
};

export const isErrorWithError = (e: any) => {
  return has(e, "error");
};

export const parseApiError = (error: any) => {
  if (isErrorWithMessageObject(error)) {
    return error?.error?.message;
  } else if (isErrorWithDataAndError(error)) {
    return error?.data?.error;
  } else if (isErrorWithError(error)) {
    return error?.error;
  } else if (error?.message) {
    return error?.message;
  }

  return null;
};

export const parseFormApiErrorValue = (
  error: Record<string, string | string[]>,
  name: string,
  defaultValue?: any,
) => {
  if (isArray(error) && error.length > 0) {
    const errObj = error.find((err) => err && has(err, name));
    const errorMessage = errObj ? get(errObj, name) : undefined;
    return errorMessage;
  }
  return get(error, name, defaultValue ?? "");
};

/**
 * An entry of an error list as text: a string as it is, an object by its
 * `message` (else `detail`) when that is a string; anything else unchanged.
 */
const errorEntryText = (entry: unknown): unknown => {
  if (entry && typeof entry === "object" && !Array.isArray(entry)) {
    const { message, detail } = entry as { message?: unknown; detail?: unknown };
    if (typeof message === "string") return message;
    if (typeof detail === "string") return detail;
  }
  return entry;
};

/**
 * `error.message` readable: a list keeps its texts and turns `{message}`
 * objects into theirs; a lone `{message}` object becomes its text.
 */
const readableMessage = (message: unknown): any =>
  Array.isArray(message) ? message.map(errorEntryText) : errorEntryText(message);

export const parseServerError = (
  error: ServerError,
  default_message: string,
): string | Record<string, any> | string[] => {
  let error_message;

  if (error?.status === 500) {
    error_message = defaultErrorMessage;
  } else if (error?.error?.message) {
    error_message = readableMessage(error.error.message);
  } else if (error?.error?.error) {
    error_message = error?.error?.error;
  } else if (error?.error) {
    error_message = error.error;
  } else if (error?.message) {
    error_message = error?.message;
  } else if (error?.detail) {
    error_message = error.detail;
  } else if (error?.data) {
    error_message = parseServerError(error.data, default_message);
  } else {
    error_message = default_message;
  }

  return error_message;
};

export const catchErrorHandler = (
  e: string[] | string | object,
  errorFn: (message: string) => void,
) => {
  const errorMessage = parseServerError(e as ServerError, defaultErrorMessage);
  if (isArray(errorMessage)) {
    errorMessage.forEach((err: any) =>
      // An entry with no text of its own never reads "[object Object]".
      errorFn(
        err !== null && typeof err === "object"
          ? defaultErrorMessage
          : String(err ?? defaultErrorMessage),
      ),
    );
  } else if (typeof errorMessage === "string") {
    errorFn(errorMessage);
  } else {
    console.error("errorMessage", errorMessage);
    errorFn(defaultErrorMessage);
  }
};
